import { eventToAvi } from '@confluence/pubsub-client/entry-points/PubSubClient.ts';
import { getPubSubClient } from '@confluence/pubsub-client/entry-points/client.ts';
import type { AddCommentType } from '@confluence/comment-context/entry-points/NewCommentsContext.ts';
import type {
	CommentData,
	ReplyData,
	CommentsDataMap,
	CommentsDataState,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { GeneralCommentLocation } from '@confluence/comments-data/entry-points/inline-comments-queries.ts';
import {
	CommentActionType,
	CommentType,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import {
	type CommentsSectionGetSingleCommentQuery as CommentsSectionGetSingleCommentQueryType,
	type CommentsSectionGetSingleCommentQueryVariables,
} from '@confluence/page-comments-queries/entry-points/__types__/CommentsSectionGetSingleCommentQuery.ts';
import {
	type ActivePageCommentQueryType,
	type ActivePageCommentQueryVariablesType,
} from '@confluence/page-comments-queries/entry-points/__types__/ActivePageCommentQuery.ts';
import { type SubscriptionsDispatchType } from '@confluence/graphql/entry-points/subscriptionTypes.ts';
import { CommentsSectionGetSingleCommentQuery } from '@confluence/page-comments-queries/entry-points/CommentsSectionGetSingleCommentQuery.graphql.ts';
import { ActivePageCommentQuery } from '@confluence/page-comments-queries/entry-points/ActivePageCommentQuery.graphql.ts';
import type { ResolvedProperties } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type { UnreadCommentIDSetOperation } from '@confluence/unread-comments/entry-points/unreadCommentsTypes.ts';
import {
	isValidUnreadComment,
	buildValidationObjectFromProps,
} from '@confluence/unread-comments/entry-points/unreadCommentsUtils.ts';
import { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import {
	createCommentsPubSubExperienceTracker,
	CommentsPubSubSource,
} from '../utils/commentsPubSubExperienceTracking.ts';
import { shouldSkipOwnPubSubEvent } from '../utils/pubsubEventGuards.ts';

import type { PageCommentsReloadFn } from './pageCommentsOperations.ts';
import { updateComment, reloadAndAddPageComments } from './pageCommentsOperations.ts';

type CombinedResolvedProperties = {
	inlineResolveProperties?: ResolvedProperties; // inline
	inlineText?: string; // inline
	resolved?: boolean; // footer
	resolvedFriendlyDate: string | null | undefined; // footer
	resolvedTime?: number | null; // footer
	resolvedUser?: string | null; // footer
};

// Shared callback type definitions
interface CommentCallbacks {
	addNewComment?: ({ currentUserMadeComment, newCommentId }: AddCommentType) => void;
	removeComment?: (commentId?: string) => void;
	addNewCommentThreads?: (newCommentThreads: CommentsDataMap) => void;
	addReplyToCommentThread?: (threadKey: string, reply: ReplyData, commentType: CommentType) => void;
	updateCommentBody?: (
		commentId: string,
		threadKey: string,
		newBodyValue: string,
		commentType: CommentType,
		displayName?: string | null,
	) => void;
	updateRemovedCommentIdsMap?: (props: {
		threadKey: string;
		commentId: string;
		commentActionType: CommentActionType.DELETE_COMMENT | CommentActionType.RESOLVE_COMMENT_THREAD;
		isReply: boolean;
		commentType: CommentType;
		resolvedProperties?: CombinedResolvedProperties;
	}) => void;
	reopenParentComment?: (props: {
		threadKey: string;
		commentType: CommentType;
		parentCommentId?: string;
	}) => void;
	getCurrentCommentsPanelState?: () => CommentsDataState;
	updateUnreadCommentIDSet?: (
		unreadCommentIDSet: Set<string>,
		operation?: UnreadCommentIDSetOperation,
	) => void;
	getViewedCommentsCacheOnly?: () => { success: boolean; data?: Set<string>; error?: Error };
	addCommentInCache?: ({
		newComment,
		onError,
	}: {
		newComment: any;
		onError?: (error: Error) => void;
	}) => void;
	deleteCommentInCache?: ({
		commentId,
		onError,
	}: {
		commentId: string;
		onError?: (error: Error) => void;
	}) => boolean;
	clearReplyIfRemoved?: (props: { commentId: string }) => void;
}

type OnPubSubEventFn = (
	input: {
		userId: string | null;
		contentId: string;
		reloader?: PageCommentsReloadFn;
		updateQueryVariables?: {
			contentId: string;
			isReactionsEnabled: boolean;
		};
	} & CommentCallbacks,
) => (event: string, payload: any) => void;

type AddSubscriberPubsubParams = {
	addSubscriber?: SubscriptionsDispatchType['addSubscriber'];
	setContentType?: SubscriptionsDispatchType['setContentType'];
	setContentId?: SubscriptionsDispatchType['setContentId'];
} & PubSubParams;

type RemoveSubscriberPubsubParams = {
	removeSubscriber?: SubscriptionsDispatchType['removeSubscriber'];
	subscriptions: Array<{ subscriberId: string; delta: string }>;
} & PubSubParams;

type PubSubParams = {
	contentId: string;
	contentType?: string | null;
	userId: string | null;
	reloader: PageCommentsReloadFn;
	addNewComment: ({ currentUserMadeComment, newCommentId }: AddCommentType) => void;
	removeComment: (commentId?: string) => void;
	queryArgs: {
		isReactionsEnabled: boolean;
	};
} & CommentCallbacks;

const FETCH_COMMENT_RETRY_DELAYS_MS = [500, 1000, 2000];

/**
 * Fetches a comment by ID with automatic retry on null result.
 * The pubsub event can arrive before the server has committed the comment,
 * causing fetchCommentById to return null. We retry with backoff to handle this.
 */
const fetchCommentByIdWithRetry = async ({
	commentId,
	contentId,
}: {
	commentId: string;
	contentId: string;
}): ReturnType<typeof fetchCommentById> => {
	let result = await fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' });
	if (result !== null) {
		return result;
	}

	for (const delayMs of FETCH_COMMENT_RETRY_DELAYS_MS) {
		// eslint-disable-next-line no-await-in-loop
		await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
		// eslint-disable-next-line no-await-in-loop
		result = await fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' });
		if (result !== null) {
			return result;
		}
	}

	return result;
};

export const fetchComment = async (
	contentId: string,
	commentId: string,
): Promise<CommentsSectionGetSingleCommentQueryType> => {
	const queryVariables: CommentsSectionGetSingleCommentQueryVariables = {
		commentId,
		contentId,
	};
	const commentDataResult = await queryInterop<CommentsSectionGetSingleCommentQueryType>({
		query: CommentsSectionGetSingleCommentQuery,
		fetchPolicy: 'network-only',
		variables: queryVariables,
	});
	return commentDataResult.data;
};

// temporary new query to fetch a page comment until the new state management rearchitecture is complete
export const fetchActivePageComment = async (
	contentId: string,
	commentId: string,
): Promise<ActivePageCommentQueryType> => {
	const queryVariables: ActivePageCommentQueryVariablesType = {
		commentId,
		contentId,
	};
	const commentDataResult = await queryInterop<ActivePageCommentQueryType>({
		query: ActivePageCommentQuery,
		fetchPolicy: 'network-only',
		variables: queryVariables,
	});
	return commentDataResult.data;
};

export const handleAddNewComment = async ({
	commentId,
	contentId,
	addNewCommentThreads,
	addReplyToCommentThread,
}: {
	commentId: string | undefined;
	contentId: string;
	addNewCommentThreads: ((newCommentThreads: CommentsDataMap) => void) | undefined;
	addReplyToCommentThread:
		| ((threadKey: string, reply: ReplyData, commentType: CommentType) => void)
		| undefined;
}): Promise<void> => {
	const commentThreadDataResult = await fetchComment(contentId, commentId ?? '');
	const comment = commentThreadDataResult?.comments?.nodes?.[0];
	if (comment) {
		const createdAt = comment.version.when;
		const commentLocation = comment.location as GeneralCommentLocation;

		// Parent comment case
		if (!comment.parentId) {
			addNewCommentThreads &&
				addNewCommentThreads({
					general: {
						[comment.id]: {
							...comment,
							isOpen: true,
							wasRemovedByAnotherUser: false,
							type: CommentType.GENERAL,
							replies: [], // Newly created comments will never have replies
							contributors: (comment as any).contributors ?? [],
							createdAt: {
								value: createdAt,
							},
							location: {
								type: 'FOOTER',
								commentResolveProperties: commentLocation.commentResolveProperties,
							},
							links: {
								webui: comment.links.webui,
								editui: null,
							},
							permissions: {
								isEditable: comment.permissions.isEditable,
								isRemovable: comment.permissions.isRemovable,
								isResolvable: true,
							},
						} as CommentData,
					},
					inline: {},
				});
		}
		// Reply case
		else {
			const threadKey = comment.ancestors[comment.ancestors.length - 1]?.id ?? comment.id;

			addReplyToCommentThread &&
				addReplyToCommentThread(
					threadKey,
					{
						...comment,
						isOpen: true,
						wasRemovedByAnotherUser: false,
						type: CommentType.GENERAL,
						createdAt: {
							value: createdAt,
						},
						location: {
							type: 'FOOTER',
							commentResolveProperties: commentLocation.commentResolveProperties,
						},
						links: {
							webui: comment.links.webui,
							editui: null,
						},
						permissions: {
							isEditable: comment.permissions.isEditable,
							isRemovable: comment.permissions.isRemovable,
							isResolvable: true,
						},
					} as ReplyData,
					CommentType.GENERAL,
				);
		}
	}
};

export const handleUpdateComment = async ({
	commentId,
	contentId,
	updateCommentBody,
}: {
	commentId: string | undefined;
	contentId: string;
	updateCommentBody:
		| ((
				commentId: string,
				threadKey: string,
				newBodyValue: string,
				commentType: CommentType,
				displayName?: string | null,
		  ) => void)
		| undefined;
}): Promise<void> => {
	const commentThreadDataResult = await fetchComment(contentId, commentId ?? '');
	const comment = commentThreadDataResult?.comments?.nodes?.[0];

	if (comment) {
		const threadKey = comment.ancestors[comment.ancestors.length - 1]?.id ?? comment.id;

		updateCommentBody &&
			updateCommentBody(
				comment.id,
				threadKey,
				comment.body.value,
				CommentType.GENERAL,
				comment.version?.by?.displayName,
			);
	}
};

export const handleResolveComment = async ({
	commentId,
	contentId,
	updateRemovedCommentIdsMap,
}: {
	commentId: string | undefined;
	contentId: string;
	updateRemovedCommentIdsMap:
		| ((props: {
				threadKey: string;
				commentId: string;
				commentActionType:
					| CommentActionType.DELETE_COMMENT
					| CommentActionType.RESOLVE_COMMENT_THREAD;
				isReply: boolean;
				commentType: CommentType;
				resolvedProperties?: CombinedResolvedProperties;
		  }) => void)
		| undefined;
}): Promise<void> => {
	const commentThreadDataResult = await fetchComment(contentId, commentId ?? '');
	const comment = commentThreadDataResult?.comments?.nodes?.[0];
	if (
		comment &&
		// @ts-expect-error
		comment.location.__typename === 'FooterComment' &&
		comment.location.commentResolveProperties &&
		fg('confluence_gen_comments_resolve_pubsub')
	) {
		updateRemovedCommentIdsMap &&
			updateRemovedCommentIdsMap({
				threadKey: comment.id,
				commentId: comment.id,
				commentActionType: CommentActionType.RESOLVE_COMMENT_THREAD,
				isReply: !!comment.parentId,
				commentType: CommentType.GENERAL,
				resolvedProperties: comment.location.commentResolveProperties,
			});
	} else if (comment) {
		updateRemovedCommentIdsMap &&
			updateRemovedCommentIdsMap({
				threadKey: comment.id,
				commentId: comment.id,
				commentActionType: CommentActionType.RESOLVE_COMMENT_THREAD,
				isReply: !!comment.parentId,
				commentType: CommentType.GENERAL,
			});
	}
};
export const handleReopenComment = async ({
	commentId,
	contentId,
	addNewCommentThreads,
	getCurrentCommentsPanelState,
}: {
	commentId: string | undefined;
	contentId: string;
	addNewCommentThreads: ((newCommentThreads: CommentsDataMap) => void) | undefined;
	getCurrentCommentsPanelState: (() => CommentsDataState) | undefined;
}) => {
	if (!getCurrentCommentsPanelState) {
		return;
	}
	const { commentsDataMap } = getCurrentCommentsPanelState();

	// Use the new ActivePageCommentQuery
	const commentThreadDataResult = await fetchActivePageComment(contentId, commentId ?? '');

	const comment = commentThreadDataResult?.comments?.nodes?.[0];
	if (comment && commentsDataMap) {
		const commentsDataMapClone = { ...commentsDataMap };
		const commentInMap = commentsDataMapClone[CommentType.GENERAL][comment.id];
		if (commentInMap) {
			commentInMap.isOpen = true;
			commentInMap.wasRemovedByAnotherUser = false;
			addNewCommentThreads && addNewCommentThreads(commentsDataMapClone);
		}
	}

	return comment;
};

const handleUnreadCommentsForReopenedComment = ({
	comment,
	userId,
	viewedComments,
	updateUnreadCommentIDSet,
}: {
	comment: any;
	userId: string;
	viewedComments: Set<string>;
	updateUnreadCommentIDSet: (
		unreadCommentIDSet: Set<string>,
		operation?: UnreadCommentIDSetOperation,
	) => void;
}) => {
	const validUnreadCommentIds = new Set<string>();

	// Validate the main comment
	const commentForValidation = buildValidationObjectFromProps({
		commentId: comment.id,
		userId: comment.author?.__typename === 'KnownUser' ? comment.author.accountId : undefined,
		createdAt: comment.createdAt?.value || undefined,
		commentType: 'FOOTER', // Page comments are always FOOTER type
		resolveProperties: { resolved: false }, // reopened comment = resolved false
	});

	if (
		isValidUnreadComment({
			comment: commentForValidation,
			currentUserId: userId,
			viewedComments,
		})
	) {
		validUnreadCommentIds.add(comment.id);
	}

	// Validate replies if they exist
	if (comment.replies) {
		comment.replies.forEach((reply: any) => {
			const replyForValidation = buildValidationObjectFromProps({
				commentId: reply.id,
				userId: reply.author?.__typename === 'KnownUser' ? reply.author.accountId : undefined,
				createdAt: reply.createdAt?.value || undefined,
				commentType: 'FOOTER', // Page comments are always FOOTER type
				resolveProperties: { resolved: false }, // reopened comment = resolved false
			});

			if (
				isValidUnreadComment({
					comment: replyForValidation,
					currentUserId: userId,
					viewedComments,
				})
			) {
				validUnreadCommentIds.add(reply.id);
			}
		});
	}

	updateUnreadCommentIDSet(validUnreadCommentIds, 'add');
};

export const handleDeletePanelComment = async ({
	commentId,
	updateRemovedCommentIdsMap,
	getCurrentCommentsPanelState,
}: {
	commentId: string | undefined;
	updateRemovedCommentIdsMap:
		| ((props: {
				threadKey: string;
				commentId: string;
				commentActionType:
					| CommentActionType.DELETE_COMMENT
					| CommentActionType.RESOLVE_COMMENT_THREAD;
				isReply: boolean;
				commentType: CommentType;
				resolvedProperties?: CombinedResolvedProperties;
		  }) => void)
		| undefined;
	getCurrentCommentsPanelState: (() => CommentsDataState) | undefined;
}): Promise<void> => {
	if (commentId) {
		// Default to code to delete the top-level comment
		let threadKey = commentId;
		let isReply = false;

		// This should always be true, but if it isn't don't break
		// This case handles removing replies in the panel
		if (getCurrentCommentsPanelState) {
			const { commentsDataMap } = getCurrentCommentsPanelState();

			// If the comment is not in the general map, it's a reply
			// That means we need to loop through the flat arrays of replies for each parent comment to find our id
			// It's not fast since it potentially checks every comment on the page, but it's better than a network call
			if (!commentsDataMap[CommentType.GENERAL][commentId]) {
				const allGeneralComments = commentsDataMap[CommentType.GENERAL];
				for (const parentComment of Object.values(allGeneralComments)) {
					if (parentComment.replies) {
						for (const reply of parentComment.replies) {
							if (reply.id === commentId) {
								threadKey = parentComment.id;
								isReply = true;
								break;
							}
						}
					}
				}
			}
		}

		updateRemovedCommentIdsMap &&
			updateRemovedCommentIdsMap({
				threadKey,
				commentId,
				commentActionType: CommentActionType.DELETE_COMMENT,
				isReply,
				commentType: CommentType.GENERAL,
			});
	}
};

export const onEvent: OnPubSubEventFn =
	({
		userId,
		contentId,
		reloader,
		addNewComment,
		removeComment,
		updateQueryVariables,
		addNewCommentThreads,
		addReplyToCommentThread,
		updateCommentBody,
		updateRemovedCommentIdsMap,
		reopenParentComment,
		getCurrentCommentsPanelState,
		getViewedCommentsCacheOnly,
		updateUnreadCommentIDSet,
		addCommentInCache,
		deleteCommentInCache,
		clearReplyIfRemoved,
	}) =>
	(event, payload) => {
		if (!event) {
			return;
		}
		let commentRequestBody;

		const { accountId } = payload;
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		if (fg('cc_fps_migration_comments') && fg('confluence_enable_subscriptions-context-provider')) {
			commentRequestBody = payload?.[event];
		} else {
			commentRequestBody = payload.commentRequestBody;
		}
		const { commentId } = commentRequestBody;
		const isReply = commentRequestBody?.pageCommentType === 'REPLY';

		// We don't need to respond to events that the current user initiated, unless we need to process their reply.
		if (
			shouldSkipOwnPubSubEvent({
				accountId,
				currentUserId: userId,
				isReply,
				isOwnReplyPubSubProcessingEnabled:
					expVal('cc_comments_pubsub_process_own_replies', 'isEnabled', false) &&
					expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
			})
		) {
			return;
		}

		const {
			start: startCommentsPubSubExperience,
			succeed: succeedCommentsPubSubExperience,
			fail: failCommentsPubSubExperience,
		} = createCommentsPubSubExperienceTracker(CommentsPubSubSource.PageCommentsPubSub);

		startCommentsPubSubExperience(event);

		try {
			switch (event) {
				case 'commentCreated':
				case 'avi:confluence:created:comment':
					if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
						if (addCommentInCache && commentId) {
							const fetchFn = expVal('cc_comments_page_comment_retry', 'isEnabled', false)
								? fetchCommentByIdWithRetry
								: ({
										commentId: cId,
										contentId: cntId,
									}: {
										commentId: string;
										contentId: string;
									}) =>
										fetchCommentById({
											commentId: cId,
											contentId: cntId,
											fetchPolicy: 'network-only',
										});
							void fetchFn({ commentId, contentId })
								.then((comment) => {
									if (comment) {
										addCommentInCache({
											newComment: comment,
											onError: failCommentsPubSubExperience,
										});
										succeedCommentsPubSubExperience();
									} else {
										failCommentsPubSubExperience(
											new Error(`commentCreated: comment ${commentId} not found after retries`),
										);
									}
								})
								.catch((err: unknown) => {
									failCommentsPubSubExperience(err);
								});
						} else {
							failCommentsPubSubExperience(
								new Error('commentCreated: addCommentInCache or commentId is not defined'),
							);
						}
					} else if (!reloader || !addNewComment) {
						failCommentsPubSubExperience(
							new Error('commentCreated: reloader or addNewComment is not defined'),
						);
					} else {
						void Promise.all([
							handleAddNewComment({
								commentId,
								contentId,
								addNewCommentThreads,
								addReplyToCommentThread,
							}),
							reloadAndAddPageComments(reloader, addNewComment, [{ commentId, accountId }], userId),
						])
							.then(() => {
								succeedCommentsPubSubExperience();
							})
							.catch((err: unknown) => {
								failCommentsPubSubExperience(err);
							});
					}

					if (updateUnreadCommentIDSet && accountId !== userId) {
						updateUnreadCommentIDSet(new Set([commentId]), 'add');
					}

					break;
				case 'commentResolved':
				case 'avi:confluence:resolved:comment':
					if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
						if (commentId) {
							// fetchCommentById implicitly updates useAllCommentsDataQuery cache
							void fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' })
								.then(() => {
									clearReplyIfRemoved?.({ commentId });
									// Handle unread comments after fetchCommentById completes
									const { replies = [] } = commentRequestBody;

									if (updateUnreadCommentIDSet) {
										updateUnreadCommentIDSet(new Set([commentId, ...replies]), 'remove');
									}
								})
								.then(() => {
									succeedCommentsPubSubExperience();
								})
								.catch((err: unknown) => {
									failCommentsPubSubExperience(err);
								});
						} else {
							failCommentsPubSubExperience(new Error('commentResolved: commentId is not defined'));
						}
						// When cc_comments_new_comments_data_manager is enabled, only commentActionType and commentId are used
						// The rest of the variables here are defaults
						if (updateRemovedCommentIdsMap && commentId) {
							updateRemovedCommentIdsMap({
								threadKey: commentId,
								commentId,
								commentActionType: CommentActionType.RESOLVE_COMMENT_THREAD,
								isReply: false,
								commentType: CommentType.GENERAL,
							});
						}
					} else {
						if (reloader) {
							void reloader()
								.then(() =>
									handleResolveComment({
										commentId,
										contentId,
										updateRemovedCommentIdsMap,
									}).then(() => {
										// Handle unread comments after reloader completes
										const { replies = [] } = commentRequestBody;

										if (updateUnreadCommentIDSet) {
											updateUnreadCommentIDSet(new Set([commentId, ...replies]), 'remove');
										}
									}),
								)
								.then(() => {
									succeedCommentsPubSubExperience();
								})
								.catch((err: unknown) => {
									failCommentsPubSubExperience(err);
								});
						} else {
							failCommentsPubSubExperience(new Error('commentResolved: reloader is not defined'));
						}
					}

					break;
				case 'commentDeleted':
				case 'avi:confluence:deleted:comment':
					if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
						if (deleteCommentInCache && commentId) {
							const deletionSuccessful = deleteCommentInCache({
								commentId,
								onError: failCommentsPubSubExperience,
							});

							if (deletionSuccessful) {
								clearReplyIfRemoved?.({ commentId });
							}
						}

						if (updateUnreadCommentIDSet) {
							updateUnreadCommentIDSet(new Set([commentId]), 'remove');
						}
						// When cc_comments_new_comments_data_manager is enabled, only commentActionType and commentId are used
						// The rest of the variables here are defaults
						if (updateRemovedCommentIdsMap && commentId) {
							updateRemovedCommentIdsMap({
								threadKey: commentId,
								commentId,
								commentActionType: CommentActionType.DELETE_COMMENT,
								isReply: false,
								commentType: CommentType.GENERAL,
							});
						}
						succeedCommentsPubSubExperience();
					} else if (!reloader) {
						failCommentsPubSubExperience(new Error('commentDeleted: reloader is not defined'));
					} else {
						void Promise.all([
							handleDeletePanelComment({
								commentId,
								updateRemovedCommentIdsMap,
								getCurrentCommentsPanelState,
							}),
							reloader().then(() => {
								removeComment && removeComment(commentId);

								if (updateUnreadCommentIDSet) {
									updateUnreadCommentIDSet(new Set([commentId]), 'remove');
								}
							}),
						])
							.then(() => {
								succeedCommentsPubSubExperience();
							})
							.catch((err: unknown) => {
								failCommentsPubSubExperience(err);
							});
					}

					break;
				case 'commentReopened':
				case 'avi:confluence:reopen:comment':
					if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
						if (commentId) {
							// fetchCommentById implicitly updates useAllCommentsDataQuery cache
							void fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' })
								.then((commentData) => {
									// Handle unread comments with the fetched comment data
									if (
										updateUnreadCommentIDSet &&
										commentData &&
										userId &&
										getViewedCommentsCacheOnly
									) {
										const viewedCommentsResult = getViewedCommentsCacheOnly();
										if (viewedCommentsResult.success && viewedCommentsResult.data) {
											handleUnreadCommentsForReopenedComment({
												comment: commentData,
												userId,
												viewedComments: viewedCommentsResult.data,
												updateUnreadCommentIDSet,
											});
										}
									}
								})
								.then(() => {
									succeedCommentsPubSubExperience();
								})
								.catch((err: unknown) => {
									failCommentsPubSubExperience(err);
								});
						} else {
							failCommentsPubSubExperience(new Error('commentReopened: commentId is not defined'));
						}
					} else {
						if (reloader) {
							void reloader()
								.then(async () => {
									const comment = await handleReopenComment({
										commentId,
										contentId,
										addNewCommentThreads,
										getCurrentCommentsPanelState,
									});

									// Handle unread comments with the existing comment data
									if (updateUnreadCommentIDSet && comment && userId && getViewedCommentsCacheOnly) {
										const viewedCommentsResult = getViewedCommentsCacheOnly();
										if (viewedCommentsResult.success && viewedCommentsResult.data) {
											handleUnreadCommentsForReopenedComment({
												comment,
												userId,
												viewedComments: viewedCommentsResult.data,
												updateUnreadCommentIDSet,
											});
										}
									}
								})
								.then(() => {
									succeedCommentsPubSubExperience();
								})
								.catch((err: unknown) => {
									failCommentsPubSubExperience(err);
								});
						} else {
							failCommentsPubSubExperience(new Error('commentReopened: reloader is not defined'));
						}
					}
					// Remove from commentThreadsRemovedByOtherUsers when reopened
					if (commentId && reopenParentComment) {
						reopenParentComment({
							threadKey: commentId,
							commentType: CommentType.GENERAL,
							parentCommentId: commentId,
						});
					}
					break;
				case 'commentUpdated':
				case 'avi:confluence:updated:comment':
					if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
						if (commentId) {
							// fetchCommentById implicitly updates useAllCommentsDataQuery cache
							void fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' })
								.then(() => {
									succeedCommentsPubSubExperience();
								})
								.catch((err: unknown) => {
									failCommentsPubSubExperience(err);
								});
						} else {
							failCommentsPubSubExperience(new Error('commentUpdated: commentId is not defined'));
						}
					} else if (updateQueryVariables) {
						void Promise.all([
							handleUpdateComment({ commentId, contentId, updateCommentBody }),
							updateComment(commentId, updateQueryVariables),
						])
							.then(() => {
								succeedCommentsPubSubExperience();
							})
							.catch((err: unknown) => {
								failCommentsPubSubExperience(err);
							});
					} else {
						failCommentsPubSubExperience(
							new Error('commentUpdated: updateQueryVariables is not defined'),
						);
					}
					break;
				default:
					failCommentsPubSubExperience(new Error(`Unhandled comments pubsub event: ${event}`));
					break;
			}
		} catch (error: unknown) {
			failCommentsPubSubExperience(error);
			throw error;
		}
	};

export const setUpPageCommentsPubSub = ({
	contentId,
	contentType,
	userId,
	reloader,
	addNewComment,
	removeComment,
	queryArgs: { isReactionsEnabled },
	addNewCommentThreads,
	addReplyToCommentThread,
	updateCommentBody,
	updateRemovedCommentIdsMap,
	reopenParentComment,
	getCurrentCommentsPanelState,
	addSubscriber,
	setContentType,
	setContentId,
	updateUnreadCommentIDSet,
	getViewedCommentsCacheOnly,
	addCommentInCache,
	deleteCommentInCache,
	clearReplyIfRemoved,
}: AddSubscriberPubsubParams) => {
	const pubSubResourceType = contentType || 'page';
	const subscriberIds: { subscriberId: string; delta: string }[] = [];
	// eslint-disable-next-line confluence-feature-gating/no-preconditioning
	if (fg('cc_fps_migration_comments') && fg('confluence_enable_subscriptions-context-provider')) {
		if (!addSubscriber || !setContentType || !setContentId) {
			return;
		}
		setContentType(pubSubResourceType);
		setContentId(contentId);
		const subscribers = [
			{
				subscriberId: 'pageCommentsPubSubCommentCreated',
				onSubscriptionData: onEvent({
					userId,
					contentId,
					reloader,
					addNewComment,
					addNewCommentThreads,
					addReplyToCommentThread,
					updateUnreadCommentIDSet,
					addCommentInCache,
				}),
				delta: 'commentCreated',
			},
			{
				subscriberId: 'pageCommentsPubSubCommentResolved',
				onSubscriptionData: onEvent({
					userId,
					contentId,
					reloader,
					addNewComment,
					updateRemovedCommentIdsMap,
					updateUnreadCommentIDSet,
					clearReplyIfRemoved,
				}),
				delta: 'commentResolved',
			},
			{
				subscriberId: 'pageCommentsPubSubCommentDeleted',
				onSubscriptionData: onEvent({
					userId,
					contentId,
					reloader,
					removeComment,
					updateRemovedCommentIdsMap,
					getCurrentCommentsPanelState,
					updateUnreadCommentIDSet,
					deleteCommentInCache,
					clearReplyIfRemoved,
				}),
				delta: 'commentDeleted',
			},
			{
				subscriberId: 'pageCommentsPubSubCommentReopened',
				onSubscriptionData: onEvent({
					userId,
					contentId,
					reloader,
					addNewComment,
					addNewCommentThreads,
					reopenParentComment,
					getCurrentCommentsPanelState,
					getViewedCommentsCacheOnly,
					updateUnreadCommentIDSet,
				}),
				delta: 'commentReopened',
			},
			{
				subscriberId: 'pageCommentsPubSubCommentUpdated',
				onSubscriptionData: onEvent({
					userId,
					contentId,
					updateQueryVariables: {
						contentId,
						isReactionsEnabled,
					},
					updateCommentBody,
				}),
				delta: 'commentUpdated',
			},
		];
		for (const subscriber of subscribers) {
			subscriberIds.push({ subscriberId: addSubscriber(subscriber), delta: subscriber.delta });
		}
		return subscriberIds;
	} else {
		void getPubSubClient().then((client) => {
			client
				.joinChannel(pubSubResourceType, contentId)
				.finally(() => {
					void client.subscribe(
						eventToAvi('created:comment'),
						onEvent({
							userId,
							contentId,
							reloader,
							addNewComment,
							addNewCommentThreads,
							addReplyToCommentThread,
							updateUnreadCommentIDSet,
							addCommentInCache,
						}),
					);
					void client.subscribe(
						eventToAvi('resolved:comment'),
						onEvent({
							userId,
							contentId,
							reloader,
							addNewComment,
							updateRemovedCommentIdsMap,
							updateUnreadCommentIDSet,
						}),
					);
					void client.subscribe(
						eventToAvi('deleted:comment'),
						onEvent({
							userId,
							contentId,
							reloader,
							removeComment,
							updateRemovedCommentIdsMap,
							getCurrentCommentsPanelState,
							updateUnreadCommentIDSet,
							deleteCommentInCache,
						}),
					);
					void client.subscribe(
						eventToAvi('reopen:comment'),
						onEvent({
							userId,
							contentId,
							reloader,
							addNewComment,
							addNewCommentThreads,
							reopenParentComment,
							getCurrentCommentsPanelState,
							getViewedCommentsCacheOnly,
							updateUnreadCommentIDSet,
						}),
					);
					void client.subscribe(
						eventToAvi('updated:comment'),
						onEvent({
							userId,
							contentId,
							updateQueryVariables: {
								contentId,
								isReactionsEnabled,
							},
							updateCommentBody,
						}),
					);
				})
				.catch((error) => {
					throw new Error(`Unable to joinChannel ${JSON.stringify(error)}`);
				});
		});
	}
};

export const removePageCommentsPubSub = ({
	contentId,
	contentType,
	userId,
	reloader,
	addNewComment,
	removeComment,
	queryArgs: { isReactionsEnabled },
	removeSubscriber,
	subscriptions,
	getViewedCommentsCacheOnly,
}: RemoveSubscriberPubsubParams): void => {
	const pubSubResourceType = contentType || 'page';
	const eventsToUnsubscribe = ['commentCreated', 'commentDeleted', 'commentUpdated'];

	// eslint-disable-next-line confluence-feature-gating/no-preconditioning
	if (fg('cc_fps_migration_comments') && fg('confluence_enable_subscriptions-context-provider')) {
		if (!removeSubscriber || !subscriptions?.length) {
			return;
		}
		for (const subscriber of subscriptions) {
			if (eventsToUnsubscribe.includes(subscriber.delta)) {
				removeSubscriber(subscriber);
			}
		}
	} else {
		void getPubSubClient().then((client) => {
			void client.leaveChannel(pubSubResourceType, contentId);

			void client.unsubscribe(
				eventToAvi('created:comment'),
				onEvent({ userId, contentId, reloader, addNewComment, getViewedCommentsCacheOnly }),
			);
			void client.unsubscribe(
				eventToAvi('deleted:comment'),
				onEvent({ userId, contentId, reloader, removeComment, getViewedCommentsCacheOnly }),
			);
			void client.unsubscribe(
				eventToAvi('updated:comment'),
				onEvent({
					userId,
					contentId,
					updateQueryVariables: {
						contentId,
						isReactionsEnabled,
					},
					getViewedCommentsCacheOnly,
				}),
			);
		});
	}
};
